import { useEffect, useMemo, useState } from "react";
import type { StoreApi } from "zustand";
import { revalidateLogic, useForm } from "@tanstack/react-form";
import { z } from "zod";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  createPermissionEvaluator,
  resolveLandingRoute,
  resolveFeatureNavigation,
  useAuthStore,
  useTenant,
  type AuthController,
  type AuthState,
  type FeatureRegistry,
} from "@nasam/core";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PasswordField,
} from "@nasam/ui";

import { authController as defaultAuthController } from "../../auth";
import { featureRegistry as defaultModuleRegistry } from "../../registry";
import { LoginArtwork } from "./LoginArtwork";

export interface LoginScreenProps {
  readonly authController?: AuthController;
  readonly authStore?: StoreApi<AuthState>;
  readonly registry?: FeatureRegistry;
}

/**
 * The art panel is a 751x976 Figma frame whose decorations are absolutely
 * placed. Percentages of that frame keep every element on its designed anchor
 * while the column scales below the 1400px design width.
 */
function RequiredMark() {
  return (
    <span className="text-fail-text" aria-hidden="true">
      *
    </span>
  );
}

export function LoginScreen({
  authController = defaultAuthController,
  authStore = useAuthStore,
  registry = defaultModuleRegistry,
}: LoginScreenProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRtl = i18n.dir() === "rtl";
  const { branding } = useTenant();

  const [logoutReason, setLogoutReason] = useState(
    () => authStore.getState().logoutReason,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    return authStore.subscribe((state) => {
      setLogoutReason(state.logoutReason);
    });
  }, [authStore]);

  const schema = useMemo(
    () =>
      z.object({
        username: z.string().min(1, t("validation:usernameRequired")),
        password: z.string().min(1, t("validation:passwordRequired")),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const onSubmit = async (values: FormValues) => {
    setErrorMessage(null);
    try {
      const result = await authController.login(values);
      if (result.requiresOtp) {
        void navigate({ to: "/auth/verify-otp" });
        return;
      }
      const evaluator = createPermissionEvaluator(result.user.permissions);
      const visibleNavigation = resolveFeatureNavigation(
        registry.list(),
        evaluator,
      );
      const target = resolveLandingRoute(result.user, visibleNavigation);
      void navigate({ to: target });
    } catch (err) {
      setErrorMessage(
        err instanceof Error
          ? err.message
          : t("authentication:invalidCredentials"),
      );
    }
  };

  const form = useForm({
    defaultValues: {
      username: "",
      password: "",
    } satisfies FormValues,
    validationLogic: revalidateLogic({ mode: "change" }),
    validators: { onDynamic: schema },
    onSubmit: ({ value }) => onSubmit(value),
  });

  useEffect(() => {
    if (!form.state.isValid) void form.validate("change");
  }, [i18n.language, form]);

  // The login route fills the viewport exactly and never scrolls: <main> is a
  // fixed-height clip box, the card spans the full width (no aspect-ratio cap),
  // and the form column compresses its vertical rhythm to fit.
  return (
    <main className="relative h-dvh overflow-hidden bg-page-background-light lg:flex lg:min-h-0 lg:items-center lg:justify-center lg:px-5 lg:py-6">
      {/* The corner glow sits under the form: bottom-right in Arabic,
          bottom-left in English (see .login-corner-glow in styles.css). */}
      <div
        aria-hidden="true"
        dir={i18n.dir()}
        className="pointer-events-none absolute inset-0"
      >
        <div
          data-node-id="2676:488094"
          className="login-corner-glow absolute bottom-0 start-0 h-[45%] w-[40%]"
        />
      </div>

      <div
        dir="ltr"
        className="relative h-full w-full lg:flex lg:h-[calc(100dvh-3rem)]"
      >
        <section
          dir={i18n.dir()}
          data-node-id="2676:155622"
          className={
            (isRtl ? "lg:order-1 " : "lg:order-2 ") +
            // Figma's split: 751 of the 1400px frame for the artwork, the
            // rest (649) for the form.
            "auth-art-panel relative hidden overflow-hidden rounded-lg bg-blue-purple-700 lg:block lg:h-full lg:w-[53.643%] lg:shrink-0"
          }
        >
          {/* The grid spans the whole panel; the artwork is Figma's 751x976
              composition fitted inside it (see "Login art" in styles.css). */}
          <div aria-hidden="true" className="login-art-grid" />
          <div dir="ltr">
            <LoginArtwork />
          </div>
        </section>

        <section
          dir={i18n.dir()}
          data-node-id="2676:155634"
          className={
            (isRtl ? "lg:order-2 " : "lg:order-1 ") +
            // No page-level scrollbar -- the inner scrollable div below
            // handles any overflow of its own.
            "auth-form-panel animate-rise flex h-full flex-col items-center overflow-hidden lg:min-h-0 lg:min-w-0 lg:flex-1 lg:rounded-lg"
          }
        >
          {/* Figma 2676:488110: from the top, the header, then the fields
              with the button right under them -- the panel's 80px padding
              and gap shrink on short screens (.auth-form-panel/-content). */}
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto">
            <div className="auth-form-content mx-auto flex w-full max-w-[40.5625rem] flex-col">
              <header className="flex w-full flex-col items-start gap-[0.3125rem] px-4 md:px-13">
                {/* Figma flips the default logo fill vertically; a tenant's own
                    logo is used as-is instead, since we don't control its art. */}
                <img
                  src={branding?.logoUrl ?? "/brand/logo.png"}
                  alt=""
                  className={
                    branding?.logoUrl === undefined
                      ? "h-[7.0625rem] w-[6.125rem] -scale-y-100"
                      : "h-[7.0625rem] w-[6.125rem] object-contain"
                  }
                />
                <div className="flex w-full flex-col items-start justify-center gap-2">
                  <div className="flex h-13 w-full items-center">
                    {/* The form's sizes are Figma's exactly (40/18/14/12):
                        login skips the app-wide 1px-smaller type tokens. */}
                    <h1 className="w-full text-start text-[2.5rem]/[1.3] font-normal text-text">
                      {t("authentication:loginTitle")}
                    </h1>
                  </div>
                  <div className="flex w-full items-center px-4">
                    <p className="flex-1 text-start text-[1.125rem]/5 text-text-placeholder">
                      {t("authentication:loginSubtitle")}
                    </p>
                  </div>
                </div>
              </header>

              <div className="flex w-full flex-col gap-6">
                <Form
                  form={form}
                  id="login-form"
                  className="flex w-full flex-col gap-8 px-4 md:px-13 py-4"
                  noValidate
                >
                  {logoutReason === "timeout" && (
                    <div
                      role="status"
                      className="animate-in fade-in-0 slide-in-from-top-1 rounded-md border border-container-default-border bg-container-default-background p-3 text-body-s-r text-text"
                    >
                      {t("authentication:sessionTimedOut")}
                    </div>
                  )}

                  {errorMessage !== null && (
                    <div
                      role="alert"
                      className="animate-in fade-in-0 slide-in-from-top-1 rounded-md border border-fail-text bg-container-default-background p-3 text-body-s-r text-fail-text"
                    >
                      {errorMessage}
                    </div>
                  )}

                  <form.Field name="username">
                    {(field) => (
                      <FormField field={field}>
                        <FormItem className="flex flex-col gap-2 space-y-0 animate-in fade-in-0 slide-in-from-bottom-1">
                          <FormLabel
                            htmlFor="login-username"
                            className="flex items-center gap-1 text-[0.875rem]/5"
                          >
                            {t("authentication:usernameLabel")}
                            <RequiredMark />
                          </FormLabel>
                          <FormControl>
                            <Input
                              name={field.name}
                              value={field.state.value}
                              onBlur={field.handleBlur}
                              onChange={(event) =>
                                field.handleChange(event.target.value)
                              }
                              id="login-username"
                              autoComplete="username"
                              placeholder={t(
                                "authentication:usernamePlaceholder",
                              )}
                              className="h-11 bg-container-hover px-4 py-2 text-start text-[0.75rem]/5"
                            />
                          </FormControl>
                          <FormMessage className="text-start" />
                        </FormItem>
                      </FormField>
                    )}
                  </form.Field>

                  <form.Field name="password">
                    {(field) => (
                      <FormField field={field}>
                        <FormItem className="flex flex-col gap-2 space-y-0 animate-in fade-in-0 slide-in-from-bottom-1 [animation-delay:60ms]">
                          <FormLabel
                            htmlFor="login-password"
                            className="flex items-center gap-1 text-[0.875rem]/5"
                          >
                            {t("authentication:passwordLabel")}
                            <RequiredMark />
                          </FormLabel>
                          <FormControl>
                            <PasswordField
                              name={field.name}
                              value={field.state.value}
                              onBlur={field.handleBlur}
                              onChange={(event) =>
                                field.handleChange(event.target.value)
                              }
                              id="login-password"
                              autoComplete="current-password"
                              placeholder={t(
                                "authentication:passwordPlaceholder",
                              )}
                              hidePasswordLabel={t(
                                "authentication:hidePassword",
                              )}
                              showPasswordLabel={t(
                                "authentication:showPassword",
                              )}
                              className="h-11 bg-container-hover px-4 py-2 text-start text-[0.75rem]/5"
                            />
                          </FormControl>
                          <FormMessage className="text-start" />
                          <Link
                            to="/auth/forgot-password"
                            className="text-start text-caption-s text-text hover:underline"
                          >
                            {t("authentication:forgotPasswordLink")}
                          </Link>
                        </FormItem>
                      </FormField>
                    )}
                  </form.Field>
                </Form>

                <div className="w-full px-4 py-4 md:px-13">
                  <form.Subscribe selector={(state) => state.isSubmitting}>
                    {(isSubmitting) => (
                      <Button
                        type="submit"
                        form="login-form"
                        variant="primary"
                        className="h-13 w-full text-[0.875rem]/5 font-bold"
                        disabled={isSubmitting}
                      >
                        <span
                          key={isSubmitting ? "busy" : "idle"}
                          className="animate-in fade-in-0 duration-150"
                        >
                          {isSubmitting
                            ? t("authentication:signingInButton")
                            : t("authentication:signInButton")}
                        </span>
                      </Button>
                    )}
                  </form.Subscribe>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
